import React, { useState } from 'react';
import { Phone, Mail, TrendingUp, TrendingDown, CheckCircle2, Calendar, ArrowRight, Shield, PhoneOff, Users, Clock, Moon, AlertCircle } from 'lucide-react';
import { services } from '../mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import RevenueCalculator from './RevenueCalculator';
import ScrollingRibbon from './ScrollingRibbon';
import ServicesGrid from './ServicesGrid';
import AIChatWidget from './AIChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const iconMap = {
    Phone: Phone,
    Mail: Mail,
    TrendingUp: TrendingUp
  };

  const scrollToBooking = () => {
    document.getElementById('booking').scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_clinic-ai-suite-1/artifacts/npjvge0y_SALUS%20%282%29.png"
            alt="SalusFlow Logo"
            className="logo-header"
          />
        </div>
        
        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          <a href="#services" className="text-sm font-medium hover:text-blue-600 transition-colors" style={{ color: 'hsl(var(--text-secondary))' }}>
            Services
          </a>
          <a href="#how-it-works" className="text-sm font-medium hover:text-blue-600 transition-colors" style={{ color: 'hsl(var(--text-secondary))' }}>
            How It Works
          </a>
          <a href="#about" className="text-sm font-medium hover:text-blue-600 transition-colors" style={{ color: 'hsl(var(--text-secondary))' }}>
            About
          </a>
          <Button onClick={scrollToBooking} className="btn-primary">
            Book Consultation
          </Button>
        </div>

        {/* Mobile Hamburger */}
        <button 
          className="md:hidden hamburger-menu"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <button 
              className="mobile-menu-close"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <a href="#services" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>Services</a>
            <a href="#how-it-works" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>How It Works</a>
            <a href="#about" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>About</a>
            <Button onClick={scrollToBooking} className="btn-primary w-full mt-4">
              Book Consultation
            </Button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="section pt-32 pb-20 mobile-hero-spacing" style={{ background: 'linear-gradient(to bottom, hsl(var(--primary-light)), white)' }}>
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="trust-badge">
                  <Shield className="w-4 h-4" />
                  Doctor-Built
                </span>
                <span className="trust-badge">
                  <CheckCircle2 className="w-4 h-4" />
                  GDPR Compliant
                </span>
              </div>
              <h1 className="heading-1 mb-6">
                Fill more appointments. Reactivate more patients.
              </h1>
              <p className="body-large mb-6">
                Doctor-built automation for UK medical clinics. Capture every lead without adding workload.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button onClick={scrollToBooking} className="btn-primary btn-large">
                  <Calendar className="w-5 h-5" />
                  Book Free Consultation
                </Button>
                <Button onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })} className="btn-secondary btn-large">
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/4266948/pexels-photo-4266948.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Healthcare Professional"
                className="rounded-3xl shadow-2xl w-full h-auto object-cover"
                style={{ maxHeight: '600px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Scrolling Ribbon Section - Below Hero */}
      <section style={{ 
        width: '100%', 
        padding: '2rem 0',
        background: 'linear-gradient(to bottom, white, hsl(var(--primary-light)))'
      }}>
        <div className="container" style={{ maxWidth: '100%', padding: '0 1rem' }}>
          <ScrollingRibbon />
        </div>
      </section>

      {/* Challenges Section */}
      <section className="section section-alt">
        <div className="container max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#1a202c', letterSpacing: '-0.02em' }}>
              Common Challenges Clinics Face
            </h2>
            <p className="text-xl md:text-2xl" style={{ color: '#4a5568', lineHeight: '1.6', maxWidth: '800px', margin: '0 auto' }}>
              Revenue is being lost through operational gaps. These are the most common.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="challenge-card">
              <div className="challenge-icon">
                <PhoneOff className="w-6 h-6" />
              </div>
              <h3 className="challenge-title">Missing Incoming Calls</h3>
              <p className="challenge-description">
                Potential clients calling when your team is busy or outside hours. Opportunities lost forever.
              </p>
            </div>

            <div className="challenge-card">
              <div className="challenge-icon">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="challenge-title">No Lead Follow Up System</h3>
              <p className="challenge-description">
                Enquiries come in but there's no reliable way to capture and follow up with them consistently.
              </p>
            </div>

            <div className="challenge-card">
              <div className="challenge-icon">
                <TrendingDown className="w-6 h-6" />
              </div>
              <h3 className="challenge-title">Website Not Converting</h3>
              <p className="challenge-description">
                Visitors land on your site but leave without booking. Interest that never turns into appointments.
              </p>
            </div>

            <div className="challenge-card">
              <div className="challenge-icon">
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="challenge-title">Manual Booking Processes</h3>
              <p className="challenge-description">
                Valuable staff time spent on repetitive admin tasks instead of patient care.
              </p>
            </div>

            <div className="challenge-card">
              <div className="challenge-icon">
                <Moon className="w-6 h-6" />
              </div>
              <h3 className="challenge-title">Lost After Hours Enquiries</h3>
              <p className="challenge-description">
                Patients trying to reach you outside working hours with no way to capture their details.
              </p>
            </div>

            <div className="challenge-card">
              <div className="challenge-icon">
                <AlertCircle className="w-6 h-6" />
              </div>
              <h3 className="challenge-title">Overwhelmed Reception</h3>
              <p className="challenge-description">
                Front desk stretched thin, trying to juggle phones, bookings, and walk ins all at once.
              </p>
            </div>
          </div>

          <div className="text-center mt-16">
            <Button onClick={scrollToBooking} className="btn-primary btn-large" style={{ fontSize: '1.125rem', padding: '18px 36px' }}>
              See How We Can Help
            </Button>
          </div>
        </div>
      </section>

      {/* Revenue Calculator Section */}
      <section className="section">
        <div className="container max-w-6xl">
          <RevenueCalculator onBookConsultation={scrollToBooking} />
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">
              Simple Implementation. Lasting Results.
            </h2>
            <p className="body-large max-w-2xl mx-auto">
              From first call to full automation in under a month
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="heading-3 mb-3 text-lg">Discovery Call</h3>
              <p className="body-medium" style={{ color: 'hsl(var(--text-secondary))' }}>
                We assess your practice needs and identify automation opportunities
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="heading-3 mb-3 text-lg">Custom Setup</h3>
              <p className="body-medium" style={{ color: 'hsl(var(--text-secondary))' }}>
                We build and configure your systems while training your team
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="heading-3 mb-3 text-lg">Go Live</h3>
              <p className="body-medium" style={{ color: 'hsl(var(--text-secondary))' }}>
                Launch with ongoing support and optimization
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Everything handled for you */}
      <ServicesGrid onBookConsultation={scrollToBooking} />

      {/* About Section - REBUILT WITH STRICT TWO-COLUMN GRID */}
      <section id="about" className="section section-alt">
        <div className="container" style={{ maxWidth: '1400px', padding: '0 1.5rem' }}>
          <div className="text-center mb-12">
            <span className="trust-badge mb-6 inline-block" style={{ fontSize: '0.9375rem', padding: '0.625rem 1.25rem' }}>
              <Shield className="w-4 h-4" />
              Built by a doctor, designed for real clinical workflows
            </span>
          </div>

          {/* SINGLE COLUMN LAYOUT WITH IMAGE */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: window.innerWidth > 968 ? '3rem' : '2rem',
            alignItems: 'center',
            width: '100%',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {/* Profile Image - Centered */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}>
              <div 
                style={{
                  width: window.innerWidth > 768 ? '280px' : '220px',
                  height: window.innerWidth > 768 ? '280px' : '220px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 20px 60px rgba(33, 150, 243, 0.3)',
                  border: '6px solid white',
                  transition: 'all 0.4s ease',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 28px 80px rgba(33, 150, 243, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(33, 150, 243, 0.3)';
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_clinic-ai-suite-1/artifacts/n1entam3_Linkedin%20headshot.jpg"
                  alt="Dr. Hamza Anwar, NHS Resident Doctor"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    margin: 0,
                    padding: 0
                  }}
                />
              </div>
            </div>

            {/* Text Content - Centered */}
            <div style={{
              textAlign: 'center',
              width: '100%'
            }}>
              <h2 style={{
                fontSize: window.innerWidth > 768 ? '2rem' : '1.75rem',
                fontWeight: '700',
                marginBottom: '2rem',
                color: '#1a202c',
                letterSpacing: '-0.01em'
              }}>
                About the Founder
              </h2>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
                fontSize: window.innerWidth > 768 ? '1.125rem' : '1rem',
                lineHeight: '1.8',
                color: '#2d3748',
                width: '100%',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, width: '100%' }}>
                  I'm <strong>Dr. Hamza Anwar</strong>, an NHS resident doctor with clinical experience across primary and secondary care settings.
                </p>

                <p style={{ margin: 0, width: '100%' }}>
                  I've worked in busy clinics where I've seen how operational gaps lead to missed appointments, lost revenue, and overwhelmed teams. I've also been involved in <strong>medtech innovation spaces</strong> and healthcare system discussions, giving me insight into both clinical workflows and the technology that can improve them.
                </p>

                <p style={{ margin: 0, width: '100%' }}>
                  I understand clinical environments. I understand the systems that fail them. And I build the solutions that work.
                </p>
              </div>
            </div>
          </div>

          {/* CENTERED CTA BUTTON - BELOW GRID */}
          <div style={{ textAlign: 'center', marginTop: '3rem', width: '100%' }}>
            <Button onClick={scrollToBooking} className="btn-primary" style={{ 
              fontSize: window.innerWidth > 768 ? '1.125rem' : '1rem', 
              padding: window.innerWidth > 768 ? '18px 40px' : '14px 28px', 
              minHeight: window.innerWidth > 768 ? '60px' : '50px',
              width: window.innerWidth <= 768 ? '100%' : 'auto',
              maxWidth: window.innerWidth <= 768 ? '300px' : 'none'
            }}>
              Book Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Calendly Booking Section */}
      <section id="booking" className="section">
        <div className="container max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="heading-2 mb-4">
              Book a Free Consultation
            </h2>
            <p className="body-large">
              Choose a time that works for you. No forms, just book directly into my calendar
            </p>
          </div>

          <div className="calendly-container">
            <iframe
              src="https://calendly.com/hamza-salusflow/30min"
              width="100%"
              height="700"
              frameBorder="0"
              title="Book a consultation"
              className="calendly-iframe"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-centered">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_clinic-ai-suite-1/artifacts/npjvge0y_SALUS%20%282%29.png"
                alt="SalusFlow Logo"
                className="logo-footer"
              />
            </div>
            <p className="footer-copyright">
              © 2024 SalusFlow. AI Solutions for UK Medical Clinics.
            </p>
            <div className="footer-links">
              <a href="#privacy" className="footer-link">
                Privacy Policy
              </a>
              <span className="footer-divider">•</span>
              <a href="#terms" className="footer-link">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Premium Chat Widget removed in favor of BuildMyAgent embed (index.html) */}

      {/* Native SalusFlow AI Chat Widget */}
      <AIChatWidget />

      {/* Try the Agent - floating phone CTA (bottom-left, opposite chat widget) */}
      <a
        href="tel:+447476928576"
        data-testid="try-the-agent-btn"
        className="try-agent-fab"
        aria-label="Call the AI receptionist to try it live"
      >
        <Phone className="w-5 h-5" aria-hidden="true" />
        <span>Try the Agent</span>
      </a>
    </div>
  );
};

export default Home;
